import React, { useState } from 'react';
import Editor from '@monaco-editor/react';
import { useTheme } from '../../contexts/ThemeContext';
import './SnippetViewer.css';

function normalizeLanguageForMonaco(lang) {
  if (!lang) return 'plaintext';
  const l = String(lang).toLowerCase();

  if (l.includes('c++') || l === 'cpp') return 'cpp';
  if (l.includes('c#') || l === 'csharp') return 'csharp';
  if (l === 'c') return 'c';
  if (l === 'js' || l.includes('javascript')) return 'javascript';
  if (l === 'ts' || l.includes('typescript')) return 'typescript';
  if (l.includes('python') || l === 'py') return 'python';
  if (l.includes('java')) return 'java';
  if (l.includes('ruby')) return 'ruby';
  if (l === 'go' || l.includes('golang')) return 'go';
  if (l.includes('php')) return 'php';
  if (l.includes('rust')) return 'rust';
  if (l.includes('kotlin')) return 'kotlin';
  if (l.includes('swift')) return 'swift';
  if (l.includes('sql')) return 'sql';
  if (l.includes('html')) return 'html';
  if (l.includes('css')) return 'css';
  if (l.includes('shell') || l.includes('bash')) return 'shell';

  return l;
}

const SnippetViewer = ({
  snippet,
  cookbook,
  isLiked,
  importing,
  isActionDisabled,
  onBack,
  onLike,
  onImport,
}) => {
  const [activeTab, setActiveTab] = useState('Code');
  const monacoLanguage = normalizeLanguageForMonaco(snippet.language);
  const { isLight } = useTheme();

  return (
    <div className="snippet-viewer">
      <div className="snippet-viewer__nav">
        <button
          type="button"
          className="snippet-viewer__back"
          onClick={onBack}
          aria-label="Back to cookbook"
        >
          <i className="ti ti-arrow-left" aria-hidden="true" />
          <span>{cookbook.name}</span>
        </button>

        <div className="snippet-viewer__actions">
          <button
            type="button"
            className={`snippet-viewer__btn-like${isLiked ? ' snippet-viewer__btn-like--liked' : ''}`}
            onClick={onLike}
            disabled={isActionDisabled}
            aria-label={isLiked ? 'Unlike' : 'Like'}
          >
            <i className={`ti ${isLiked ? 'ti-heart-filled' : 'ti-heart'}`} aria-hidden="true" />
            {isLiked ? 'Liked' : 'Like'}
          </button>

          <button
            type="button"
            className="snippet-viewer__btn-import"
            onClick={onImport}
            disabled={isActionDisabled || importing}
            aria-label="Import into your library"
          >
            <i className={`ti ti-${importing ? 'loader' : 'download'}`} aria-hidden="true" />
            {importing ? 'Importing…' : 'Import'}
          </button>
        </div>
      </div>

      <div className="snippet-viewer__title-row">
        <h2 className="snippet-viewer__title">{snippet.title}</h2>
        <div className="snippet-viewer__tab-group">
          {['Code', 'Notes'].map((tab) => (
            <button
              key={tab}
              type="button"
              className={`snippet-viewer__tab${activeTab === tab ? ' snippet-viewer__tab--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="snippet-viewer__divider" />

      {activeTab === 'Code' ? (
        <div className="snippet-viewer__content">
          <Editor
            height="100%"
            language={monacoLanguage}
            value={snippet.code ?? ''}
            theme={isLight ? 'vs' : 'vs-dark'}
            options={{
              readOnly: true,
              minimap: { enabled: false },
              scrollBeyondLastLine: false,
              fontSize: 13,
              lineHeight: 22,
              padding: { top: 12, bottom: 12 },
              renderLineHighlight: 'none',
              overviewRulerLanes: 0,
              hideCursorInOverviewRuler: true,
              scrollbar: { verticalScrollbarSize: 4 },
              wordWrap: 'on',
            }}
          />
        </div>
      ) : (
        <div className="snippet-viewer__notes">
          {snippet.notes ? (
            <p>{snippet.notes}</p>
          ) : (
            <span className="snippet-viewer__notes-empty">No notes for this snippet.</span>
          )}
        </div>
      )}
    </div>
  );
};

export default SnippetViewer;
